import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type { OptionsMap } from '@openx/types/options';
import type { JSX, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Item, type ItemProps } from './Item';
import {
  StyledAddIcon,
  StyledContainer,
  StyledIconContainer,
  StyledItemsRemoveIcon,
  StyledItemsRemoveIconButton,
  StyledSpan,
  StyledTitleWrapper,
} from './styled/Items.styled';
import { Title } from './Title';
import { itemToString } from './utils/utils';

export type SelectedItemComponent = React.FC<ItemProps<string | JSX.Element>>;

type ItemsProps = {
  onRemove?: (id: string | JSX.Element) => void;
  onRemoveAll: () => void;
  ItemComponent?: SelectedItemComponent;
  selectedItems: (string | JSX.Element)[];
  hideTitle?: boolean;
  title: string;
  isAddIcon: boolean;
  hideIcon?: boolean;
  readonly?: boolean;
  options?: OptionsMap;
  'data-test'?: string;
  disabledStyle: boolean;
  isRemovingAll?: boolean;
  opSwitch?: ReactElement;
  customSubtitle?: string;
  isMinimalAudiencePreview?: boolean;
};

export const Items = ({
  ItemComponent = Item,
  selectedItems,
  onRemove,
  onRemoveAll,
  hideTitle = false,
  title,
  isAddIcon = true,
  hideIcon = false,
  readonly,
  'data-test': dataTest,
  options,
  disabledStyle,
  isRemovingAll = false,
  opSwitch,
  customSubtitle,
  isMinimalAudiencePreview,
}: ItemsProps): JSX.Element => {
  const { t } = useTranslation();
  const Icon = isAddIcon ? StyledAddIcon : StyledItemsRemoveIcon;
  const defaultTitle = t('Included');

  return (
    <Grid container data-test="section-items">
      {!hideTitle && (
        <StyledTitleWrapper data-test="action-subtitle">
          <Title
            title={title || defaultTitle}
            data-test={`${(dataTest ?? 'tf').toLocaleLowerCase().replace(/[\s_]/g, '-')}-allow`}
          />

          {!readonly && isRemovingAll && (
            <StyledItemsRemoveIconButton
              className="removeIconButton"
              onClick={onRemoveAll}
              size="large"
              data-test="items-remove-all"
            >
              <CloseIcon data-test="close-dialog" fontSize="small" />
            </StyledItemsRemoveIconButton>
          )}
        </StyledTitleWrapper>
      )}

      {customSubtitle && (
        <Typography
          variant="inherit"
          data-test={`${(dataTest ?? 'tf').toLocaleLowerCase().replace(/[\s_]/g, '-')}-subtitle`}
          sx={{
            color: 'text.secondary',
            width: '100%',
          }}
        >
          {customSubtitle}
        </Typography>
      )}

      {!readonly && opSwitch}
      {selectedItems.length > 0 && (
        <StyledContainer container sx={{ flexGrow: 1 }}>
          {!hideIcon && (
            <StyledIconContainer container>
              <StyledSpan>
                <Icon
                  fontSize="small"
                  disabledStyle={disabledStyle}
                  data-test={isAddIcon ? 'add-icon' : 'remove-icon'}
                />
              </StyledSpan>
            </StyledIconContainer>
          )}

          <Grid
            container
            data-test="items-container"
            sx={{
              flexGrow: 1,
              width: 'fit-content',
            }}
          >
            {selectedItems.map((item, index) => {
              const key = itemToString(item);

              return (
                <ItemComponent
                  key={key}
                  testId={`${(dataTest ?? 'tf').toLocaleLowerCase().replace(/\s/g, '-').replaceAll('_', '-')}-item`}
                  readonly={!!readonly}
                  item={item}
                  option={options?.[key]}
                  onRemove={onRemove}
                  disabledStyle={disabledStyle}
                  isMinimalAudiencePreview={isMinimalAudiencePreview}
                >
                  {options?.[key]?.name ?? item}
                  {!opSwitch && `${index < selectedItems.length - 1 ? ',' : ''}`}
                </ItemComponent>
              );
            })}
          </Grid>
        </StyledContainer>
      )}
    </Grid>
  );
};
